import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { invokeRunFlowRuntime } from '@/lib/runtime-worker-client';
import type { DashboardChatLaunchProposalResult } from '@shared/types/dashboard-chat';

/**
 * I1: the chat proposes to launch an app on the text the person gave. The
 * inputs are those of their last launch, editable; nothing runs before the
 * click. The tool reads the text instead of the web, then its list, next
 * actions and Mon travail follow as for any launch.
 */
export const AppChatLaunchCard = ({ result }: { result: DashboardChatLaunchProposalResult }) => {
  const [values, setValues] = useState<Record<string, string>>(
    () => Object.fromEntries(result.inputs.map((input) => [input.key, input.value ?? ''])),
  );
  const [state, setState] = useState<{ status: 'idle' | 'busy' | 'done'; executionId: string | null; error: string | null }>(
    { status: 'idle', executionId: null, error: null },
  );
  const missing = result.inputs.some((input) => input.required && !values[input.key]?.trim());
  const lines = result.sourceText.split('\n').filter((line) => line.trim()).length;

  const launch = () => {
    setState({ status: 'busy', executionId: null, error: null });
    const inputValues = Object.fromEntries(Object.entries(values).filter(([, value]) => value.trim()));
    void invokeRunFlowRuntime({ flowId: result.flowId, inputValues, sourceText: result.sourceText })
      .then((response) => {
        const executionId = (response as { executionId?: unknown } | null)?.executionId;
        if (typeof executionId !== 'string') throw new Error('Le lancement n’a pas démarré.');
        setState({ status: 'done', executionId, error: null });
      })
      .catch((cause) => setState({ status: 'idle', executionId: null, error: cause instanceof Error ? cause.message : 'Le lancement n’a pas démarré.' }));
  };

  return (
    <div className="mt-3 space-y-3 rounded-2xl border border-domino-cardBorder bg-domino-white px-3 py-3 shadow-sm">
      <div>
        <p className="text-sm font-semibold text-domino-ink">{result.appTitle}</p>
        <details className="mt-1 text-xs text-domino-inkSoft">
          <summary className="cursor-pointer">Texte fourni · {lines} ligne{lines > 1 ? 's' : ''}</summary>
          <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap font-sans text-domino-ink">{result.sourceText}</pre>
        </details>
      </div>
      {result.inputs.length > 0 && (
        <div className="space-y-2">
          {result.inputs.map((input) => (
            <label key={input.key} className="block text-xs text-domino-inkSoft">
              {input.label}
              <Input
                className="mt-1"
                value={values[input.key] ?? ''}
                disabled={state.status !== 'idle'}
                onChange={(event) => setValues((current) => ({ ...current, [input.key]: event.target.value }))}
              />
            </label>
          ))}
        </div>
      )}
      {state.status === 'done' && state.executionId
        ? (
          <p role="status" className="text-xs text-domino-inkSoft">
            Lancé. <Link className="text-domino-aubergine underline" to={`/executions/${state.executionId}`}>Suivre le lancement</Link>
          </p>
        )
        : (
          <Button size="sm" disabled={state.status === 'busy' || missing} onClick={launch}>
            {state.status === 'busy' ? 'Lancement…' : 'Lancer sur ce texte'}
          </Button>
        )}
      {state.error && <p role="alert" className="text-xs text-domino-prune">{state.error}</p>}
    </div>
  );
};
